/**
 * Benefit validation — windows, usage caps, assignment, and abuse checks.
 *
 * Pure functions only. Callers load rows (grants, invitation codes, prior
 * redemptions) and pass them in; nothing here touches the network.
 */

import type { RegionCode } from "../pricing-types";
import { FRAUD_IP_ACCOUNT_THRESHOLD, benefitRule } from "./benefit-config";
import type {
  BenefitEligibility,
  BenefitGrant,
  BenefitRejection,
  BenefitRejectionReason,
  BenefitType,
  FraudSignal,
  InvitationCode,
  RedemptionContext,
} from "./benefit-types";

const MESSAGES: Record<BenefitRejectionReason, string> = {
  auth: "Please sign in to use this benefit.",
  not_found: "We couldn't find that code. Please double-check and try again.",
  inactive: "This benefit is no longer active.",
  revoked: "This code has been revoked. Please contact us at hello@wekit.ai.",
  expired: "This benefit has expired.",
  not_yet_valid: "This benefit isn't active yet.",
  exhausted: "This code has reached its usage limit.",
  not_assigned: "This code was issued to a different email address.",
  already_redeemed: "You've already used this benefit.",
  not_eligible: "This benefit isn't available on your account yet.",
  region_not_allowed: "This benefit isn't available for your region.",
  internal_only: "We couldn't find that code. Please double-check and try again.",
  fraud_suspected:
    "We need to check this before applying it. Please contact hello@wekit.ai.",
};

export function reject(reason: BenefitRejectionReason): BenefitRejection {
  return { reason, message: MESSAGES[reason] };
}

export interface ValidationOutcome<T> {
  ok: boolean;
  value: T | null;
  rejection: BenefitRejection | null;
}

function withinWindow(
  now: Date,
  from?: string | null,
  until?: string | null,
): { ok: boolean; reason?: "not_yet_valid" | "expired" } {
  if (from) {
    const start = new Date(from);
    if (!Number.isNaN(start.getTime()) && start > now) {
      return { ok: false, reason: "not_yet_valid" };
    }
  }
  if (until) {
    const end = new Date(until);
    if (!Number.isNaN(end.getTime()) && end < now) {
      return { ok: false, reason: "expired" };
    }
  }
  return { ok: true };
}

/** Validate an account entitlement. */
export function validateGrant(
  grant: BenefitGrant,
  opts: { now: Date; isAdmin?: boolean },
): ValidationOutcome<BenefitGrant> {
  const fail = (reason: BenefitRejectionReason): ValidationOutcome<BenefitGrant> => ({
    ok: false,
    value: null,
    rejection: reject(reason),
  });

  if (grant.status === "revoked") return fail("revoked");
  if (grant.status === "expired") return fail("expired");
  if (grant.status === "exhausted") return fail("exhausted");
  if (grant.status !== "active") return fail("inactive");

  const window = withinWindow(opts.now, grant.validFrom, grant.validUntil);
  if (!window.ok) return fail(window.reason ?? "expired");

  const rule = benefitRule(grant.benefitType);
  if (rule.internalOnly && !opts.isAdmin) return fail("internal_only");

  return { ok: true, value: grant, rejection: null };
}

/** Validate a private invitation code. */
export function validateInvitation(
  invite: InvitationCode,
  opts: {
    now: Date;
    userId?: string | null;
    email?: string | null;
    isAdmin?: boolean;
  },
): ValidationOutcome<InvitationCode> {
  const fail = (
    reason: BenefitRejectionReason,
  ): ValidationOutcome<InvitationCode> => ({
    ok: false,
    value: null,
    rejection: reject(reason),
  });

  if (invite.status === "revoked") return fail("revoked");
  if (invite.status !== "active") return fail("inactive");

  const window = withinWindow(opts.now, null, invite.expiration);
  if (!window.ok) return fail(window.reason ?? "expired");

  if (invite.uses >= Math.max(1, invite.maximumUses)) return fail("exhausted");

  if (invite.assignedTo && opts.userId && invite.assignedTo !== opts.userId) {
    return fail("not_assigned");
  }
  if (
    invite.email &&
    String(invite.email).toLowerCase() !== String(opts.email ?? "").toLowerCase()
  ) {
    return fail("not_assigned");
  }

  const rule = benefitRule(invite.benefitType);
  if (rule.internalOnly && !opts.isAdmin) return fail("internal_only");

  return { ok: true, value: invite, rejection: null };
}

/** Is this benefit type usable in this region under the region's allowed layers? */
export function isBenefitAllowedInRegion(
  type: BenefitType,
  allowedLayers: string[],
  _region: RegionCode,
): boolean {
  return allowedLayers.includes(benefitRule(type).layer);
}

/**
 * Usage-cap check across account and email, using redemptions the caller loaded.
 */
export function checkUsageCaps(
  type: BenefitType,
  benefitId: string,
  ctx: RedemptionContext,
  overrides: {
    maxUsesPerUser?: number | null;
    maxUsesPerEmail?: number | null;
  } = {},
): ValidationOutcome<true> {
  const rule = benefitRule(type);
  const perUser = overrides.maxUsesPerUser ?? rule.maxUsesPerUser;
  const prior = (ctx.priorRedemptions ?? []).filter(
    (r) => r.benefitType === type && r.benefitId === benefitId,
  );
  if (perUser != null && prior.length >= perUser) {
    return { ok: false, value: null, rejection: reject("already_redeemed") };
  }
  return { ok: true, value: true, rejection: null };
}

/**
 * Abuse signals. `block` severity means the caller must refuse the benefit;
 * `warn` surfaces in the admin Fraud Alerts panel without blocking a sale.
 */
export function detectFraudSignals(
  type: BenefitType,
  benefitId: string,
  ctx: RedemptionContext,
): FraudSignal[] {
  const signals: FraudSignal[] = [];

  const duplicates = (ctx.priorRedemptions ?? []).filter(
    (r) => r.benefitType === type && r.benefitId === benefitId,
  );
  if (duplicates.length > 0) {
    signals.push({
      code: "duplicate_redemption",
      severity: "block",
      message: "This benefit has already been redeemed on this account.",
      details: { benefitId, count: duplicates.length },
    });
  }

  if ((ctx.accountsFromSameIp ?? 0) >= FRAUD_IP_ACCOUNT_THRESHOLD) {
    signals.push({
      code: "ip_velocity",
      severity: "warn",
      message: `${ctx.accountsFromSameIp} accounts redeemed benefits from this network recently.`,
      details: { ipHash: ctx.ipHash, accounts: ctx.accountsFromSameIp },
    });
  }

  return signals;
}

export function hasBlockingSignal(signals: FraudSignal[]): boolean {
  return signals.some((s) => s.severity === "block");
}

/** Derive the convenience booleans on an eligibility object from its grants. */
export function summariseEligibility(
  eligibility: Omit<
    BenefitEligibility,
    "scholarship" | "partnerChild" | "institution" | "referral" | "ambassador" | "staff"
  > & { admin?: boolean },
): BenefitEligibility {
  const has = (t: BenefitType) =>
    eligibility.grants.some((g) => g.benefitType === t && g.status === "active");
  return {
    ...eligibility,
    admin: eligibility.admin === true,
    scholarship: has("SCHOLARSHIP"),
    partnerChild: has("PARTNER"),
    institution: has("INSTITUTION") || (eligibility.institutionIds?.length ?? 0) > 0,
    referral: has("REFERRAL"),
    ambassador: has("AMBASSADOR"),
    staff: has("STAFF"),
  };
}
